/**
 * Shared text-flow layout: the greedy word wrapper generalized to lines
 * that dodge image silhouettes ("text wrap"). Both surfaces that paint a
 * wrapped text block — the editor canvas (FlowedText) and the PDF
 * exporter — call flowText with the SAME inputs and the SAME measure
 * function, so line breaks and x-positions agree by construction instead
 * of "closely enough": the editor paints the runs with browser glyphs,
 * the PDF paints the identical runs with the embedded font.
 *
 * Everything is in spread design units (px at a SPREAD_DESIGN_W-wide
 * canvas, y down, origin at the text box's top-left) — the units font
 * sizes are stored in. Both renderers scale linearly from there, so a
 * layout computed once is valid at any output size.
 *
 * Flow model, mirroring the classic DTP wrap:
 *   • each line band [top, top+lineHeight) intersects every exclusion
 *     ring; a ring's x-extent within the band (inflated by the standoff
 *     on all sides) is forbidden,
 *   • the remaining segments of the box width are filled left to right —
 *     text continues on BOTH sides of an obstacle in the same band,
 *   • segments narrower than minSegmentEm are dropped (no one-letter
 *     slivers), fully blocked bands are skipped (text resumes below),
 *   • alignment applies per segment; explicit `\n` starts the next band;
 *     a word is never divided just because an image narrowed its segment —
 *     it moves on to the next segment or band instead. Only a word wider
 *     than the FULL box hard-breaks by characters, exactly like the
 *     legacy wrapper.
 */
import { SPREAD_TEXT_WRAP } from './spreadGeometry';

/** One laid-out run: a piece of a line placed at (x, top), y down. */
export interface FlowRun {
  text: string;
  x: number;
  top: number;
  width: number;
}

export type TextMeasure = (s: string) => number;

export interface FlowTextParams {
  text: string;
  /** Font size in design units (sets the min-segment threshold). */
  size: number;
  lineHeight: number;
  boxW: number;
  boxH: number;
  align: 'left' | 'center' | 'right';
  /** Closed silhouette rings, design units relative to the box origin
   *  (computeWrapExclusions). Empty array = plain greedy wrap. */
  exclusions: Array<Array<[number, number]>>;
  /** Width of a string at `size`, design units. */
  measure: TextMeasure;
}

/** x-extent of a closed ring within the horizontal strip [y0, y1], or
 *  null when the ring doesn't reach the strip. Conservative: concave
 *  rings block their full width, which is what a reader's eye expects
 *  from a wrap anyway. */
function ringExtentInStrip(
  ring: Array<[number, number]>,
  y0: number,
  y1: number,
): [number, number] | null {
  let minX = Infinity;
  let maxX = -Infinity;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const [ax, ay] = ring[i];
    const [bx, by] = ring[(i + 1) % n];
    const lo = Math.min(ay, by);
    const hi = Math.max(ay, by);
    if (hi < y0 || lo > y1) continue;
    if (ay === by) {
      // Horizontal edge inside the strip: both endpoints count.
      if (ax < minX) minX = ax;
      if (bx < minX) minX = bx;
      if (ax > maxX) maxX = ax;
      if (bx > maxX) maxX = bx;
      continue;
    }
    // Clip the edge to the strip and take the clipped endpoints' x.
    for (const y of [Math.max(lo, y0), Math.min(hi, y1)]) {
      const t = (y - ay) / (by - ay);
      const x = ax + (bx - ax) * t;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
    }
  }
  return minX <= maxX ? [minX, maxX] : null;
}

/** Usable [start, end] segments of the box width for one line band. */
function bandSegments(
  bandTop: number,
  bandBottom: number,
  boxW: number,
  exclusions: Array<Array<[number, number]>>,
  standoff: number,
  minSegment: number,
): Array<[number, number]> {
  if (exclusions.length === 0) return [[0, boxW]];

  // Inflating the strip vertically and the extent horizontally by the
  // standoff is a Minkowski sum with a standoff-sized square — the ring
  // itself never needs offsetting.
  const forbidden: Array<[number, number]> = [];
  for (const ring of exclusions) {
    const ext = ringExtentInStrip(ring, bandTop - standoff, bandBottom + standoff);
    if (ext) forbidden.push([ext[0] - standoff, ext[1] + standoff]);
  }
  if (forbidden.length === 0) return [[0, boxW]];

  forbidden.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [forbidden[0]];
  for (let i = 1; i < forbidden.length; i++) {
    const last = merged[merged.length - 1];
    if (forbidden[i][0] <= last[1]) last[1] = Math.max(last[1], forbidden[i][1]);
    else merged.push(forbidden[i]);
  }

  const segments: Array<[number, number]> = [];
  let cursor = 0;
  for (const [a, b] of merged) {
    if (a > cursor) segments.push([cursor, Math.min(a, boxW)]);
    cursor = Math.max(cursor, b);
    if (cursor >= boxW) break;
  }
  if (cursor < boxW) segments.push([cursor, boxW]);
  return segments.filter(([a, b]) => b - a >= minSegment);
}

/** Lay a text block out around the exclusions. Runs whose band starts at
 *  or below boxH are never produced — both renderers clip there anyway. */
export function flowText(p: FlowTextParams): FlowRun[] {
  const { boxW, boxH, lineHeight, align, exclusions, measure } = p;
  if (!p.text.trim() || boxW <= 0 || boxH <= 0 || lineHeight <= 0) return [];
  const standoff = SPREAD_TEXT_WRAP.standoff;
  const minSegment = p.size * SPREAD_TEXT_WRAP.minSegmentEm;

  const runs: FlowRun[] = [];
  let band = -1;
  let segs: Array<[number, number]> = [];
  let segIdx = 0;
  let cur = '';
  let out = false;

  // Advance to the next band with at least one usable segment.
  const nextBand = () => {
    do {
      band++;
      if (band * lineHeight >= boxH) {
        out = true;
        segs = [];
        return;
      }
      segs = bandSegments(
        band * lineHeight,
        (band + 1) * lineHeight,
        boxW,
        exclusions,
        standoff,
        minSegment,
      );
    } while (segs.length === 0);
    segIdx = 0;
  };

  const flush = () => {
    if (!cur || out) {
      cur = '';
      return;
    }
    const w = measure(cur);
    const [a, b] = segs[segIdx];
    let x = a;
    if (align === 'center') x = a + (b - a - w) / 2;
    else if (align === 'right') x = b - w;
    runs.push({ text: cur, x, top: band * lineHeight, width: w });
    cur = '';
  };

  const nextSlot = () => {
    flush();
    if (out) return;
    segIdx++;
    if (segIdx >= segs.length) nextBand();
  };

  // Last resort for a word wider than the FULL box: hard-break it by
  // characters (the legacy wrapper's rule), spilling across bands. Chunks
  // land only in full-width segments — never beside an image — so the
  // break is a box-size problem, not a wrap artifact. The final chunk
  // stays open so following words continue the line; a character wider
  // than the box is forced through (and clipped) like the legacy wrapper.
  const placeLongWord = (word: string) => {
    const chars = Array.from(word);
    let i = 0;
    while (i < chars.length && !out) {
      const [a, b] = segs[segIdx];
      if (b - a < boxW) {
        nextSlot();
        continue;
      }
      let chunk = '';
      while (i < chars.length && measure(chunk + chars[i]) <= boxW) {
        chunk += chars[i];
        i++;
      }
      if (!chunk) {
        chunk = chars[i];
        i++;
      }
      cur = chunk;
      if (i < chars.length) nextSlot();
    }
  };

  const placeWord = (word: string) => {
    while (!out) {
      const [a, b] = segs[segIdx];
      const segW = b - a;
      const candidate = cur ? `${cur} ${word}` : word;
      if (measure(candidate) <= segW) {
        cur = candidate;
        return;
      }
      if (cur) {
        // Line full — finish it and try the word in the next slot.
        nextSlot();
        continue;
      }
      if (segW >= boxW) {
        placeLongWord(word);
        return;
      }
      // The word alone doesn't fit a segment an image narrowed. Never
      // divide it — move on to the next segment (the obstacle's other
      // side, or the band below).
      nextSlot();
    }
  };

  nextBand();
  const paragraphs = p.text.split('\n');
  for (let pi = 0; pi < paragraphs.length && !out; pi++) {
    if (pi > 0) {
      // Explicit newline: finish the line and drop to the NEXT band —
      // never just the next segment of the same one.
      flush();
      nextBand();
    }
    const words = paragraphs[pi].split(/\s+/).filter(Boolean);
    for (const word of words) {
      if (out) break;
      placeWord(word);
    }
  }
  flush();
  return runs;
}

// ============================================================
// Canvas-based measurement (the shared measure for both renderers)
// ============================================================

let measureCtx: CanvasRenderingContext2D | null | undefined;

/** Word/line width caches keyed by the canvas font string. */
const widthCaches = new Map<string, Map<string, number>>();

/**
 * A TextMeasure backed by canvas measureText for the given book font at
 * `sizePx` (design units). Returns null when no 2D context is available
 * (callers fall back to their own metrics). Caller is responsible for the
 * font actually being loaded (isBookFontLoaded / loadBookFont) — measuring
 * through a fallback face would produce a layout the glyphs then betray.
 */
export function canvasTextMeasure(
  fontFamily: string,
  fontWeight: number,
  sizePx: number,
): TextMeasure | null {
  if (measureCtx === undefined) {
    measureCtx =
      typeof document === 'undefined'
        ? null
        : document.createElement('canvas').getContext('2d');
  }
  const ctx = measureCtx;
  if (!ctx) return null;

  const font = `${fontWeight} ${sizePx}px ${fontFamily}`;
  let cache = widthCaches.get(font);
  if (!cache) {
    if (widthCaches.size > 40) widthCaches.clear();
    cache = new Map();
    widthCaches.set(font, cache);
  }
  return (s: string) => {
    let w = cache.get(s);
    if (w === undefined) {
      if (cache.size > 5000) cache.clear();
      ctx.font = font;
      w = ctx.measureText(s).width;
      cache.set(s, w);
    }
    return w;
  };
}

/** CSS font shorthand used for load/check (any px size works — readiness
 *  is per face, not per size). */
const fontSpec = (fontFamily: string, fontWeight: number) =>
  `${fontWeight} 16px ${fontFamily}`;

/** Synchronous readiness check for a book font face. */
export function isBookFontLoaded(fontFamily: string, fontWeight: number): boolean {
  try {
    return document.fonts.check(fontSpec(fontFamily, fontWeight));
  } catch {
    return false;
  }
}

/** Kick (and await) loading of a book font face for measurement. */
export async function loadBookFont(fontFamily: string, fontWeight: number): Promise<void> {
  try {
    await document.fonts.load(fontSpec(fontFamily, fontWeight));
  } catch {
    // Swallow — callers degrade to their fallback metrics.
  }
}
